import { handleTransition } from 'Util/transitions.js';
import { CONST } from 'app_vodafone_ziggo/util/util';
import * as debounce from 'lodash/debounce';

const activeCardIndexes = new Map();
let isScrolling = false;

const selectors = {
    tabsLayout: '.js-tabs-layout',
    tabs: '.js-tabs-layout-tabs',
    tabsContainer: '.js-tabs-layout-tabs-container',
    tabButton: '.js-tabs-layout-tab',
    tabContent: '.js-tabs-layout-content',
    card: '.js-tabs-layout-card'
};

const classes = {
    active: 'active',
    hasOverflow: 'has-overflow'
};

const attributes = {
    tabIndex: 'data-tab-index',
    cardIndex: 'data-card-index',
    transitionType: 'data-transition-type'
};

const transitions = {
    carousel: 'carousel',
    dissolve: 'dissolve'
};

/**
 * Removes the 'active' class from all buttons and cards
 * @param {HTMLElement} tabsLayout - The container of the tab component.
 */
function removeAllActiveClasses(tabsLayout) {
    const activeElements = tabsLayout.querySelectorAll(`.${classes.active}`);

    activeElements.forEach((element) => {
        element.classList.remove(classes.active);
    });
}

/**
 * Activates the specified card and its corresponding button.
 * @param {HTMLElement} tabsLayout - The container of the tab component.
 * @param {number} targetIndex - The index of the card to activate.
 */
function activateCardAndButton(tabsLayout, targetIndex) {
    removeAllActiveClasses(tabsLayout);

    const cards = tabsLayout.querySelectorAll(selectors.card);
    const tabButtons = tabsLayout.querySelectorAll(selectors.tabButton);

    cards[targetIndex].classList.add(classes.active);
    tabButtons[targetIndex].classList.add(classes.active);
}

/**
 * @function onClickTab
 * @param {HTMLElement} tabs - All Tab Layout tab buttons
 * @param {HTMLElement} activeTab - The active tab button
 */
function handleBtnTransition(tabs, activeTab) {
    const activeTabIndex = parseInt(activeTab.getAttribute(attributes.tabIndex), 10);
    const activeTabWidth = activeTab.offsetWidth;
    let activeTabOffset = 0;

    if (activeTabIndex !== 0) {
        const tabButtons = tabs.querySelectorAll(selectors.tabButton);

        tabButtons.forEach((tabBtn) => {
            const tabIndex = parseInt(tabBtn.getAttribute(attributes.tabIndex), 10);
            if (tabIndex < activeTabIndex) activeTabOffset += tabBtn.offsetWidth + 2;
        });
    }

    tabs.style.setProperty(`--tab-width`, `${activeTabWidth}px`);
    tabs.style.setProperty('--tab-offset', `${activeTabOffset}px`);
}

/**
 * @function handleTabsOverflow
 * @param {HTMLElement} tabsLayout - The container of the tab component.
 */
function handleTabsOverflow(tabsLayout) {
    const tabsContainer = tabsLayout.querySelector(selectors.tabsContainer);
    const tabs = tabsLayout.querySelector(selectors.tabs);
    const tabsAreOverflowing = tabs.scrollWidth > tabs.clientWidth;
    let tabsWidth = '100%';

    if (tabsAreOverflowing) {
        const viewportWidth = document.documentElement.querySelector('body').clientWidth;

        tabsWidth = `${viewportWidth}px`;

        tabsContainer.classList.add(classes.hasOverflow);
    } else {
        tabsContainer.classList.remove(classes.hasOverflow);
    }

    tabs.style.setProperty('--tabs-width', tabsWidth);
}

/**
 * Scrolls to the card with the specified index inside the horizontal scrolling area.
 * @param {HTMLElement} tabsLayout - The container of the tab component.
 * @param {number} cardIndex - The index of the card to scroll to.
 */
function scrollToCard(tabsLayout, cardIndex) {
    const tabs = tabsLayout.querySelector(selectors.tabs);
    const targetCard = tabsLayout.querySelector(`${selectors.card}[${attributes.cardIndex}="${cardIndex}"]`);
    const tabContent = tabsLayout.querySelector(selectors.tabContent);
    const isCarousel = tabs.getAttribute(attributes.transitionType) === transitions.carousel;
    const scrollPosition = targetCard.offsetLeft - tabContent.offsetLeft;

    isScrolling = true;

    if (isCarousel) {
        // Carousel behaviour
        tabContent.scrollTo({
            left: scrollPosition,
            behavior: 'smooth'
        });
    } else {
        // Default (dissolve/fade) behaviour
        handleTransition(() => {
            tabContent.classList.toggle('scrolling');

            tabContent.scrollTo({
                left: scrollPosition,
                behavior: 'instant'
            });

            setTimeout(() => {
                tabContent.classList.toggle('scrolling');
            }, 150);
        });
    }

    setTimeout(() => {
        isScrolling = false;
    }, 500);

    activateCardAndButton(tabsLayout, cardIndex);
}

/**
 * Scrolls to the tab with the specified index inside the horizontal scrolling area.
 * @param {HTMLElement} tabsLayout - The container of the tab component.
 * @param {number} tabIndex - The index of the tab to scroll to.
 */
function scrollToTab(tabsLayout, tabIndex) {
    const tabs = tabsLayout.querySelector(selectors.tabs);
    const tabsAreOverflowing = tabs.scrollWidth > tabs.clientWidth;
    const tabButtons = tabsLayout.querySelectorAll(selectors.tabButton);
    const tab = tabButtons[tabIndex];

    if (tabsAreOverflowing) {
        const scrollPosition = tabButtons[tabIndex].offsetLeft - tabs.offsetLeft - 20;

        tabs.scrollTo({
            left: scrollPosition,
            behavior: 'smooth'
        });
    }

    // Button transition
    handleBtnTransition(tabs, tab);
}

/**
 * Callback function for the Intersection Observer.
 * @param {IntersectionObserverEntry[]} entries - An array of IntersectionObserverEntry objects.
 */
function onIntersection(entries) {
    entries.forEach((entry) => {
        if (entry.isIntersecting && !isScrolling) {
            const card = entry.target;
            const tabsLayout = card.closest(selectors.tabsLayout);
            const cardIndex = parseInt(card.getAttribute(attributes.cardIndex), 10);

            if (cardIndex !== activeCardIndexes.get(tabsLayout)) {
                activeCardIndexes.set(tabsLayout, cardIndex);

                activateCardAndButton(tabsLayout, cardIndex);
                scrollToTab(tabsLayout, cardIndex);
            }
        }
    });
}

/**
 * Sets up the Intersection Observer for the cards.
 * @param {HTMLElement} tabContent - The container of the cards.
 * @param {HTMLElement[]} cards - The cards.
 */
function setupIntersectionObserver(tabContent, cards) {
    const observer = new IntersectionObserver(onIntersection, {
        root: tabContent,
        threshold: 0.5
    });

    cards.forEach((card) => observer.observe(card));
}

/**
 * @function onClickTab
 * @param {Event} event - The click event
 */
function onClickTab(event) {
    const clickedTab = event.target;
    const tabsLayout = clickedTab.closest(selectors.tabsLayout);
    const clickedTabIndex = parseInt(clickedTab.getAttribute(attributes.tabIndex), 10);

    if (clickedTabIndex !== activeCardIndexes.get(tabsLayout)) {
        scrollToCard(tabsLayout, clickedTabIndex);
        scrollToTab(tabsLayout, clickedTabIndex);

        activeCardIndexes.set(tabsLayout, clickedTabIndex);
    }
}

export default (function init() {
    const tabsLayout = document.querySelectorAll(selectors.tabsLayout);

    tabsLayout.forEach((tabLayout) => {
        // Tabs overflow handler
        handleTabsOverflow(tabLayout);
        window.addEventListener(
            'resize',
            debounce(() => {
                handleTabsOverflow(tabLayout);
            }),
            300
        );

        const tabs = tabLayout.querySelector(selectors.tabs);
        const tabButtons = tabLayout.querySelectorAll(selectors.tabButton);
        const cards = tabLayout.querySelectorAll(selectors.card);
        const tabContent = tabLayout.querySelector(selectors.tabContent);

        tabButtons.forEach((tabBtn) => {
            tabBtn.addEventListener(CONST.events.click, onClickTab);
        });

        // Set index for each card
        cards.forEach((card, index) => {
            card.setAttribute(attributes.cardIndex, index);
        });

        setupIntersectionObserver(tabContent, cards);

        // Button transition
        const activeTabBtn = tabLayout.querySelector(`${selectors.tabs} .${classes.active}`);
        const activeTabBtnWidth = activeTabBtn.offsetWidth + 1;

        tabs.style.setProperty(`--tab-width`, `${activeTabBtnWidth}px`);
    });
})();
